// ============================================================
// TMR Portal Admin — a conferência do contrato antes de enviar
// ============================================================
import { Link } from 'react-router-dom';
import type { UseQueryResult } from '@tanstack/react-query';
import { ApiError } from '@tmr/api-client';
import { Field } from '../../components/ui';
import { Notice } from '../../components/Notice';
import { Skeleton } from '../../components/states';
import type {
  ContractPreview,
  ContractPreviewSignatory,
  SignatureDeliveryChannel,
} from '../../data/contracts/contract';
import { documentDisplayName } from '../../data/contracts/status';

/** Props for {@link ContractPreviewPanel}. */
interface ContractPreviewPanelProps {
  /**
   * The preview query, owned by the caller.
   *
   * It lives up there because the same answer decides two things: what this panel renders, and whether the
   * caller's "confirmar e enviar" is offered at all. Reading it in both places would resolve the Empresa
   * twice per open.
   */
  query: UseQueryResult<ContractPreview>;
  /**
   * The channel the caller has settled on, so "Quem assina" shows the contact that will actually be used.
   * Optional: a caller with no selector gets the e-mail line, which is what this panel always showed.
   */
  channel?: SignatureDeliveryChannel;
}

/**
 * What the contract would actually say — the seven values the document prints, who signs it, and what is
 * missing.
 *
 * It exists because the flow had a blind spot with real consequences: the document is generated **at the
 * moment of sending**, from the Empresa's record as it stands then, and the only thing shown before that
 * click was the Empresa's name, its CNPJ and the representative. The other five values reached a person's
 * inbox unread by anyone here.
 *
 * Everything comes from `GET /contracts/{id}/preview` already masked and laid out. Nothing is assembled
 * locally, and that is not laziness: the contract's address line is not `companyAddress`'s, so a preview
 * built here would show a document nobody is going to sign.
 *
 * A preview it cannot build is still a preview: the backend answers 200 with `ready: false` and the reason,
 * so the operator sees what to fix rather than an empty error state.
 */
export function ContractPreviewPanel({ query, channel = 'Email' }: ContractPreviewPanelProps) {
  const preview = query.data;

  if (query.isPending) return <Skeleton rows={6} />;

  if (query.isError) {
    return (
      <Notice tone="danger">
        Não foi possível montar a prévia do contrato.{' '}
        {query.error instanceof ApiError ? query.error.detail : null}{' '}
        <button type="button" onClick={() => void query.refetch()} style={inlineButtonStyle}>
          Tentar de novo
        </button>
      </Notice>
    );
  }

  if (!preview) return null;

  const blocking = preview.issues.filter((issue) => issue.blocking);
  const warnings = preview.issues.filter((issue) => !issue.blocking);

  return (
    <>
      <section>
        <h4 style={sectionTitleStyle}>O que vai no contrato</h4>
        {preview.fields.length === 0 ? (
          <p style={mutedStyle}>Sem dados para mostrar enquanto os pontos acima não forem resolvidos.</p>
        ) : (
          // Two columns where there is room (`09` §11, N3). Stacked, the seven values the contract prints
          // ran past the fold of the confirmation modal — and the whole reason this step exists is that
          // they be read before the document reaches a client. `auto-fit` collapses back to one column on
          // a narrow viewport, so nothing depends on the modal's width.
          <div style={fieldGridStyle}>
            {preview.fields.map((field) => (
              <Field key={field.key} label={field.label}>
                {field.value ?? <span style={{ color: 'var(--fg-subtle)' }}>em branco</span>}
              </Field>
            ))}
          </div>
        )}
      </section>

      {preview.signatories.length > 0 && (
        <section style={{ marginTop: 20 }}>
          <h4 style={sectionTitleStyle}>Quem assina</h4>
          {/* Keyed by role, not by e-mail: a WhatsApp signatory may not have one, and two nulls would
              collide into a single row. */}
          {preview.signatories.map((signatory) => (
            <Field key={signatory.role} label={signatoryLabel(signatory)}>
              {signatory.name}
              {signatoryContact(signatory, channel) ? ` · ${signatoryContact(signatory, channel)}` : null}
            </Field>
          ))}
        </section>
      )}

      <section style={{ marginTop: 20 }}>
        <h4 style={sectionTitleStyle}>Documento</h4>
        <Field label="Nome no provedor">
          {documentDisplayName(preview.documentName) ?? <span style={{ color: 'var(--fg-subtle)' }}>—</span>}
        </Field>
        {preview.deadlineAt && (
          <Field label="Prazo para assinar">
            {new Date(preview.deadlineAt).toLocaleDateString('pt-BR')}
          </Field>
        )}
      </section>

      {blocking.map((issue) => (
        <Notice key={issue.code + issue.message} tone="danger">
          {issue.message}{' '}
          <Link to={`/empresas/${preview.companyId}?editar=1`} style={linkStyle}>
            Corrigir o cadastro da empresa
          </Link>
        </Notice>
      ))}

      {warnings.map((issue) => (
        <Notice key={issue.code + issue.message} tone="warning">
          {issue.message}
        </Notice>
      ))}

      {/* Said once, plainly, because it is the thing nobody knew: there is no need to cancel and start over
          to fix a wrong value. Cancelling is for discarding a contract, not for correcting one. */}
      <p style={mutedStyle}>
        O documento é gerado no envio, a partir do cadastro da empresa. Corrigir a empresa e enviar depois já
        usa os dados novos —{' '}
        <Link to={`/empresas/${preview.companyId}?editar=1`} style={linkStyle}>
          abrir o cadastro
        </Link>
        .
      </p>
    </>
  );
}

const fieldGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  columnGap: 20,
};

/** How a signatory's side reads on screen. */
function signatoryLabel(signatory: ContractPreviewSignatory): string {
  if (signatory.role === 'Tmr') {
    return signatory.signsThroughApi ? 'TMR (assina automaticamente)' : 'TMR';
  }
  return 'Pela empresa';
}

/**
 * The contact this signatory will actually be reached at.
 *
 * The channel only moves the Empresa's side: the TMR's counter-signature is always by e-mail, or through
 * the API, whatever the operator picked for the client.
 */
function signatoryContact(
  signatory: ContractPreviewSignatory,
  channel: SignatureDeliveryChannel,
): string | null {
  if (signatory.role === 'Tmr') return signatory.email;
  return channel === 'WhatsApp' ? signatory.phone : signatory.email;
}

const sectionTitleStyle = {
  fontFamily: 'var(--font-sans)',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  color: 'var(--fg-subtle)',
  margin: '0 0 8px',
};

const mutedStyle = {
  margin: '14px 0 0',
  fontFamily: 'var(--font-sans)',
  fontSize: 12.5,
  lineHeight: 1.55,
  color: 'var(--fg-subtle)',
};

const linkStyle = { color: 'var(--forest-800)', fontWeight: 500 };

const inlineButtonStyle = {
  background: 'transparent',
  border: 0,
  padding: 0,
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
  fontSize: 12.5,
  color: 'var(--forest-800)',
  textDecoration: 'underline',
};
